// src/components/common/SEO.js
// Componente reutilizable para Helmet/SEO por página.
// No renderiza nada visible; solo inyecta meta tags al <head>.
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.avlinknetwork.org';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-image.png`;
const DEFAULT_TITLE = 'Avlink Network | Comunidad AV de Latinoamérica';
const DEFAULT_DESCRIPTION =
  'Comunidad profesional para profesionales de audio, video e iluminación en Latinoamérica. Cursos, certificaciones, eventos y oportunidades laborales del sector AV.';

/**
 * @param {object} props
 * @param {string} props.title         - Título de la página (sin marca; la marca se concatena automáticamente).
 * @param {string} props.description   - Meta description (~140-160 chars).
 * @param {string} [props.path]        - Path absoluto para canonical (ej: "/about").
 * @param {string} [props.image]       - URL absoluta de imagen OG (1200x630).
 * @param {string} [props.type]        - og:type (default: website).
 * @param {boolean} [props.noindex]    - Si true, agrega robots noindex.
 * @param {Array}  [props.jsonLd]      - Array de objetos JSON-LD para inyectar.
 * @param {string} [props.keywords]    - Meta keywords (opcional, no muy usado por Google pero sí Bing/Yandex).
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
  keywords
}) => {
  const fullTitle = title ? `${title} | Avlink Network` : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />
      <link rel="canonical" href={canonical} />

      <link rel="alternate" hrefLang="es" href={canonical} />
      <link rel="alternate" hrefLang="es-419" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Avlink Network" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* JSON-LD (datos estructurados) */}
      {Array.isArray(jsonLd) &&
        jsonLd.map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
};

export default SEO;
